import { useData } from 'components/common-form/hooks/useData';
import { useFieldMetadata } from 'components/common-form/hooks/useFieldMetadata';
import { useFieldTranslationKey } from 'components/common-form/hooks/useFieldTranslationKey';
import { useIsFieldInvalid } from 'components/common-form/hooks/useIsFieldInvalid';
import { usePatchedFieldHighlight } from 'components/common-form/hooks/usePatchedFieldHighlight';
import { BlockOrientationFormGroup } from 'components/shared/FormGroup';
import { SingleUnderlyingSelector } from 'components/shared/underlyings/selector/single';
import { UnderlyingsGridLegacy } from 'components/shared/underlyings/underlyingsGridLegacy';
import { useGenerateUnderlyingsGridForExcel } from 'hooks/underlyings/use-generate-underlyings-grid-for-excel';
import { useUnderlyingSearchByFormId } from 'hooks/underlyings/use-underlying-search';
import { useCopyToExcel } from 'hooks/useCopyToExcel';
import {
  DEFAULT_STRIKE_METADATA,
  type SingleUnderlyingSearchEditableFieldData,
  type SingleUnderlyingSearchEditableFieldMetadata,
} from 'models/forms/fields';
import type { Underlying } from 'models/underlying';
import type { UnderlyingStrike } from 'models/underlying/base';
import { FC, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import type { UnderlyingsFieldProps } from './UnderlyingsField.connect';
import { connectUnderlyings } from './UnderlyingsField.connect';
import { emptyUnderlyings, updateCutOff, updateStrike, updateWeight } from './utils';

const Component: FC<UnderlyingsFieldProps> = ({ node: { id }, defaultUnderlyingCodeType, tabId, tabIndex }) => {
  const { formatMessage } = useIntl();

  const [underlyings = emptyUnderlyings, setUnderlyings] =
    useData<Extract<SingleUnderlyingSearchEditableFieldData, Underlying[]>>(id);
  const isFieldInvalid = useIsFieldInvalid(id);
  const translationKey = useFieldTranslationKey(id);
  const patchedFieldClassName = usePatchedFieldHighlight(id);
  const {
    availableStrikeModes = [],
    isReadOnly,
    hasCutOff,
    strike = DEFAULT_STRIKE_METADATA,
  } = useFieldMetadata<SingleUnderlyingSearchEditableFieldMetadata>(id)!;

  const underlyingSearch = useUnderlyingSearchByFormId(tabId, id);

  const { underlyingsGroupedByAssetType, generateTableExport } = useGenerateUnderlyingsGridForExcel(underlyings);

  const assetTypes = useMemo(() => Array.from(underlyingsGroupedByAssetType.keys()), [underlyingsGroupedByAssetType]);

  const onUnderlyingRemoved = useCallback(
    (idToRemove: string) =>
      setUnderlyings((underlyings || emptyUnderlyings).filter(underlying => underlying.id !== idToRemove)),
    [underlyings, setUnderlyings],
  );

  const onUnderlyingReplaced = useCallback(
    (idToReplace: string, newUnderlying: Underlying) =>
      setUnderlyings(
        (underlyings || emptyUnderlyings).map(underlying =>
          underlying.id === idToReplace ? newUnderlying : underlying,
        ),
      ),
    [underlyings, setUnderlyings],
  );

  const onUnderlyingStrikeChanged = useCallback(
    (underlyingId: string, key: keyof UnderlyingStrike, value: unknown) =>
      setUnderlyings(
        (underlyings || emptyUnderlyings).map(underlying =>
          underlying.id === underlyingId ? updateStrike(underlying, key, value) : underlying,
        ),
      ),
    [underlyings, setUnderlyings],
  );

  const onUnderlyingWeightChanged = useCallback(
    (underlyingId: string, value: number) =>
      setUnderlyings(
        (underlyings || emptyUnderlyings).map(underlying =>
          underlying.id === underlyingId ? updateWeight(underlying, value) : underlying,
        ),
      ),
    [underlyings, setUnderlyings],
  );

  const onUnderlyingCutOffChanged = useCallback(
    (underlyingId: string, value: string) =>
      setUnderlyings(
        (underlyings || emptyUnderlyings).map(underlying =>
          underlying.id === underlyingId ? updateCutOff(underlying, value) : underlying,
        ),
      ),
    [underlyings, setUnderlyings],
  );

  const onCopyAll = useCopyToExcel(generateTableExport);

  const placeholder = useMemo(() => formatMessage({ id: 'underlying.placeholder' }), [formatMessage]);
  return (
    <>
      <BlockOrientationFormGroup inputId={id} label={`form.${translationKey}` as any} className={patchedFieldClassName}>
        {!isReadOnly && (
          <SingleUnderlyingSelector
            id={id}
            {...underlyingSearch}
            underlying={underlyings.at(0)}
            onChange={underlying => setUnderlyings(underlying ? [underlying] : [])}
            placeholder={placeholder}
            hasErrors={isFieldInvalid}
            defaultUnderlyingCodeType={defaultUnderlyingCodeType}
            tabIndex={tabIndex}
          />
        )}
      </BlockOrientationFormGroup>
      <UnderlyingsGridLegacy
        fieldId={id}
        formId={tabId}
        strikeMetadata={strike}
        strikeModes={availableStrikeModes}
        hasCutOff={hasCutOff}
        isReadOnly={isReadOnly}
        isUserInternal
        underlyings={underlyings}
        assetTypes={assetTypes}
        underlyingsGroupedByAssetType={underlyingsGroupedByAssetType}
        onUnderlyingRemoved={onUnderlyingRemoved}
        onUnderlyingReplaced={onUnderlyingReplaced}
        onUnderlyingStrikeChanged={onUnderlyingStrikeChanged}
        onUnderlyingWeightChanged={onUnderlyingWeightChanged}
        onUnderlyingCutOffChanged={onUnderlyingCutOffChanged}
        onCopyAll={onCopyAll}
        defaultUnderlyingCodeType={defaultUnderlyingCodeType}
      />
    </>
  );
};

Component.displayName = 'RawSingleUnderlyingField';

export const SingleUnderlyingField = connectUnderlyings(Component);
SingleUnderlyingField.displayName = 'SingleUnderlyingField';
